import React, { createContext, useCallback, useEffect, useState } from 'react';
import { BlockerFunction, useBlocker, useLocation } from 'react-router-dom';

import { Modal } from 'hooks/useModal.types';
import { doModalListenersBlockClose } from 'hooks/useModalPrompt';

interface ModalContextType {
    modals: Modal[];
    showModal: (modal: Modal) => void;
    closeModal: () => void;
    closeAllModals: () => void;
    isModalOpen: boolean;
}

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [modals, setModals] = useState<Modal[]>([]);

    useBlocker(
        useCallback<BlockerFunction>(
            ({ historyAction }) => {
                // 'Back' navigtation should just close the top modal
                if (historyAction === 'POP' && modals.length > 0) {
                    closeModal();

                    return true;
                }

                return false;
            },
            [modals],
        ),
    );

    const showModal = (modal: Modal) => {
        setModals((prevModals) => [...prevModals, modal]);
    };

    const applyCurrentOnClose = () => {
        const currentModal = modals[modals.length - 1];
        if (currentModal && currentModal.onClose) {
            currentModal.onClose();
        }
    };

    const closeModal = async () => {
        if (await doModalListenersBlockClose()) {
            return;
        }

        applyCurrentOnClose();

        // Close top modal
        setModals((prevModals) => {
            return prevModals.slice(0, prevModals.length - 1);
        });
    };

    const closeAllModals = () => {
        // Todo: Apply onClose + listeners
        modals.forEach((modal) => modal.onClose?.());
        setModals([]);
    };

    const isModalOpen = modals.length > 0;

    useEffect(() => {
        return () => {
            closeModal();
        };
    }, [location.pathname, location.search]);

    return (
        <ModalContext.Provider value={{ modals, showModal, closeModal, closeAllModals, isModalOpen }}>
            {children}
        </ModalContext.Provider>
    );
};
